import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { 
  getUserBillingSettings, 
  updateBillingSettings, 
  getServerPaymentMethods,
  getServerBillingInvoices
} from '../api/billing';

interface BillingSettings {
  spending_limit_usd: number;
  alert_threshold_percent: number;
  auto_suspend: boolean;
}

interface PaymentMethod {
  id: string;
  type: string;
  last4: string;
  brand: string;
  exp_month: number;
  exp_year: number;
  is_default?: boolean;
}

interface BillingContextType {
  billingSettings: BillingSettings;
  paymentMethods: PaymentMethod[];
  billingInvoices: any[];
  settingsLoading: boolean;
  paymentMethodsLoading: boolean;
  invoicesLoading: boolean;
  loadBillingData: () => Promise<void>;
  updateBillingSettingsData: (settings: Partial<BillingSettings>) => Promise<void>;
  refreshPaymentMethods: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

interface BillingProviderProps {
  children: ReactNode;
}

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const [billingSettings, setBillingSettings] = useState<BillingSettings>({
    spending_limit_usd: 50,
    alert_threshold_percent: 80,
    auto_suspend: true
  });
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [billingInvoices, setBillingInvoices] = useState<any[]>([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const isLoadingRef = useRef(false); // Use ref to prevent concurrent loads without triggering re-renders
  const loadedUserIdRef = useRef<string | null>(null); // Track which user's data has been loaded
  const { user } = useAuth();

  const loadBillingData = useCallback(async () => {
    if (!user) {
      return;
    }

    // Prevent concurrent loads
    if (isLoadingRef.current) {
      console.log('[Billing] Already loading, skipping...');
      return;
    }

    isLoadingRef.current = true;
    try {
      // Load billing settings
      try {
        const settings = await getUserBillingSettings(user.id);
        if (settings) {
          setBillingSettings({
            spending_limit_usd: settings.spending_limit_usd,
            alert_threshold_percent: settings.alert_threshold_percent,
            auto_suspend: settings.auto_suspend
          });
        }
      } catch (settingsError) {
      }

      // Load payment methods from server
      setPaymentMethodsLoading(true);
      try {
        const paymentMethodsData = await getServerPaymentMethods(user.id);
        setPaymentMethods(paymentMethodsData.map(pm => ({
          id: pm.id,
          type: pm.type,
          last4: pm.last4 || '',
          brand: pm.brand || '',
          exp_month: pm.exp_month || 0,
          exp_year: pm.exp_year || 0,
          is_default: pm.is_default
        })));
      } catch (error) {
        console.error('Failed to load payment methods from server:', error);
        // If it's a "customer not found" error, that's expected for new users
        if (error instanceof Error && error.message.includes('Customer not found')) {
          // User needs to set up billing first
          setPaymentMethods([]);
        } else {
          setPaymentMethods([]);
        }
      } finally {
        setPaymentMethodsLoading(false);
      }

      // Load billing invoices
      setInvoicesLoading(true);
      try {
        const invoices = await getServerBillingInvoices(user.id);
        setBillingInvoices(invoices);
      } catch (error) {
        console.error('Failed to load billing invoices:', error);
        // If it's a "customer not found" error, that's expected for new users
        if (error instanceof Error && error.message.includes('Customer not found')) {
          // User needs to set up billing first
        }
      } finally {
        setInvoicesLoading(false);
      }
    } catch (error) {
      console.error('❌ Failed to load billing data:', error);
    } finally {
      isLoadingRef.current = false;
    }
  }, [user]);

  const updateBillingSettingsData = useCallback(async (newSettings: Partial<BillingSettings>) => {
    if (!user) return;

    const updatedSettings = {
      ...billingSettings,
      ...newSettings
    };

    setBillingSettings(updatedSettings);
    setSettingsLoading(true);

    try {
      await updateBillingSettings(user.id, {
        spending_limit_usd: updatedSettings.spending_limit_usd,
        alert_threshold_percent: updatedSettings.alert_threshold_percent,
        auto_suspend: updatedSettings.auto_suspend
      });
    } catch (error) {
      console.error('Failed to save billing settings:', error);
      // Revert the change on error
      setBillingSettings(billingSettings);
      throw error;
    } finally {
      setSettingsLoading(false);
    }
  }, [user, billingSettings]);

  const refreshPaymentMethods = useCallback(async () => {
    if (!user) return;

    setPaymentMethodsLoading(true);
    try {
      const paymentMethodsData = await getServerPaymentMethods(user.id);
      setPaymentMethods(paymentMethodsData.map(pm => ({
        id: pm.id,
        type: pm.type,
        last4: pm.last4 || '',
        brand: pm.brand || '',
        exp_month: pm.exp_month || 0,
        exp_year: pm.exp_year || 0,
        is_default: pm.is_default
      })));
    } catch (error) {
      console.error('Failed to refresh payment methods:', error);
      if (error instanceof Error && error.message.includes('Customer not found')) {
        // User needs to set up billing first
        setPaymentMethods([]);
      } else {
        setPaymentMethods([]);
      }
    } finally {
      setPaymentMethodsLoading(false);
    }
  }, [user]);

  // Load billing data only once when user is available and data hasn't been loaded yet
  useEffect(() => {
    // Reset dataLoaded if user changed
    if (user && loadedUserIdRef.current !== null && loadedUserIdRef.current !== user.id) {
      console.log('[Billing] User changed, resetting data loaded state');
      setDataLoaded(false);
      loadedUserIdRef.current = null;
      return;
    }

    // Only load if:
    // 1. User exists
    // 2. Data hasn't been loaded for this user
    // 3. Not currently loading
    // 4. Haven't already started loading for this user
    if (user && !dataLoaded && !isLoadingRef.current && loadedUserIdRef.current !== user.id) {
      console.log('[Billing] Loading billing data for user:', user.id);
      loadedUserIdRef.current = user.id; // Mark as loading for this user
      loadBillingData().then(() => {
        setDataLoaded(true);
      }).catch((error) => {
        console.error('[Billing] Failed to load billing data:', error);
        loadedUserIdRef.current = null; // Reset on error so it can retry
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, dataLoaded]); // Only depend on user.id and dataLoaded to prevent re-runs when user object reference changes

  const value: BillingContextType = {
    billingSettings,
    paymentMethods,
    billingInvoices,
    settingsLoading,
    paymentMethodsLoading,
    invoicesLoading,
    loadBillingData,
    updateBillingSettingsData,
    refreshPaymentMethods
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};
